import { useQuery } from '@tanstack/react-query';
import { listClosureReasonsV2 } from 'services/leadServices';
import { queryKeys } from 'hooks/config/queryClient';

/**
 * @param {*} response — axios response from `listClosureReasonsV2`
 * @param {number} fallbackPage
 * @param {number} fallbackLimit
 */
function mapClosureReasonsV2Response(response, fallbackPage, fallbackLimit) {
  const raw = response?.data ?? {};
  const list = raw['closure-reason'] ?? raw.data ?? raw.items ?? [];
  const rows = Array.isArray(list) ? list : [];
  const totalCount = Number(raw.total_count ?? raw?.pagination?.total_count ?? rows.length) || 0;
  const apiPage = Number(raw.page) > 0 ? Number(raw.page) : fallbackPage;
  const apiLimit = Number(raw.limit) > 0 ? Number(raw.limit) : fallbackLimit;
  const hasNext = raw.has_next != null ? Boolean(raw.has_next) : apiPage * apiLimit < totalCount;
  const hasPrevious = raw.has_previous != null ? Boolean(raw.has_previous) : apiPage > 1;

  return { rows, totalCount, page: apiPage, limit: apiLimit, hasNext, hasPrevious };
}

export const fetchClosureReasonsListV2 = async ({
  is_lead = false,
  excludeFromLoading = false,
  limit = 20,
  page = 1,
  sort_by = '',
  sort_order = '',
  keyword = '',
  practice_area_id = '',
}) => {
  const response = await listClosureReasonsV2({
    is_lead,
    excludeFromLoading,
    limit,
    page,
    sort_by,
    sort_order,
    keyword,
    practice_area_id,
  });
  return mapClosureReasonsV2Response(response, page, limit);
};

/**
 * Fetch helper for ClosureReasonSelect (react-select-async-paginate + queryClient.fetchQuery).
 *
 * @param {Object} params
 * @param {boolean} [params.is_lead=false]
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=20]
 * @param {string} [params.sort_by='closure_reason']
 * @param {string} [params.sort_order='asc']
 * @param {string} [params.keyword='']
 * @param {string} [params.practice_area_id='']
 * @param {boolean} [params.excludeFromLoading=true]
 */
export const fetchClosureReasonsPage = async ({
  is_lead = false,
  page = 1,
  pageSize = 20,
  sort_by = 'closure_reason',
  sort_order = 'asc',
  keyword = '',
  practice_area_id = '',
  excludeFromLoading = true,
} = {}) => {
  const data = await fetchClosureReasonsListV2({
    is_lead,
    excludeFromLoading,
    limit: pageSize,
    page,
    sort_by,
    sort_order,
    keyword,
    practice_area_id,
  });

  const options = data.rows.map((row) => ({
    label: row.closure_reason,
    value: row.closure_reason,
    closure_reason: row.closure_reason,
    ...row,
  }));

  return {
    options,
    hasMore: data.hasNext,
    additional: { page },
  };
};

/**
 * Invalidate all closure-reasons-v2 queries (settings tables + ClosureReasonSelect dropdown).
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 */
export const invalidateClosureReasonsV2 = (queryClient) =>
  queryClient.invalidateQueries({
    queryKey: queryKeys.closureReasonsV2.all,
    refetchType: 'active',
  });

/**
 * Paginated closure reasons (`list-closure-reasons-v2`). Same shape as BillingV2 list hooks:
 * callers pass API `sort_by` / `sort_order` (both `''` on first load; set when the user sorts).
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.is_lead=false]
 * @param {number} [opts.limit=20]
 * @param {number} [opts.page=1]
 * @param {string} [opts.sort_by=''] — forwarded to API
 * @param {string} [opts.sort_order=''] — forwarded to API (`''`, `asc`, or `desc`)
 * @param {string} [opts.keyword=''] — keyword search (forwarded to API)
 * @param {boolean} [opts.enabled=true]
 * @param {boolean} [opts.excludeFromLoading=false]
 */
export function useClosureReasonsListV2Query({
  is_lead = false,
  limit = 20,
  page = 1,
  sort_by = '',
  sort_order = '',
  keyword = '',
  enabled = true,
  excludeFromLoading = false,
} = {}) {
  const filters = {
    is_lead,
    sort_by: sort_by ?? '',
    sort_order: sort_order ?? '',
    keyword: keyword ?? '',
  };

  const queryKey = queryKeys.closureReasonsV2.listV2(filters, page, limit);

  const query = useQuery({
    queryKey,
    queryFn: () =>
      fetchClosureReasonsListV2({
        is_lead,
        excludeFromLoading,
        limit,
        page,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
        keyword: filters.keyword,
      }),
    enabled,
    keepPreviousData: true,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  const data = query.data;

  return {
    rows: data?.rows ?? [],
    totalCount: data?.totalCount ?? 0,
    hasNext: data?.hasNext ?? false,
    hasPrevious: data?.hasPrevious ?? false,
    apiPage: data?.page ?? page,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}
